import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ChartCard,
  DataTable,
  KpiCard,
  Notes,
  formatCurrency,
  formatNumber,
  tooltipProps,
} from '../components/ChartKit';

const MODEL_HELP = {
  first_touch: 'All credit goes to the first recorded touchpoint for each won deal.',
  last_touch: 'All credit goes to the last recorded touchpoint before the deal was won.',
  linear: 'Credit is shared equally across every recorded touchpoint for each won deal.',
};

/** FR-M13-005 Attribution models (first-touch, last-touch, linear) */
const AttributionTab = ({ data, periodLabel }) => {
  const [model, setModel] = useState('first_touch');
  const current = data.models[model];
  const rows = current.rows;
  const chart = rows.slice(0, 10).map((r) => ({
    touchpoint: r.touchpoint.length > 28 ? `${r.touchpoint.slice(0, 27)}…` : r.touchpoint,
    full: r.touchpoint,
    deals: r.deals,
    revenue: r.revenueUsd,
  }));
  const top = rows[0];

  return (
    <div className="analytics-tab-body">
      <div className="analytics-segmented" role="radiogroup" aria-label="Attribution model">
        {Object.entries(data.models).map(([key, m]) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={model === key}
            className={`analytics-segment ${model === key ? 'active' : ''}`}
            onClick={() => setModel(key)}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="analytics-model-help">
        <strong>{current.label}:</strong> {MODEL_HELP[model]}
      </p>

      <div className="analytics-kpis">
        <KpiCard
          icon="fa-trophy"
          label="Deals won"
          value={formatNumber(data.wonDeals)}
          hint={periodLabel}
          tone="green"
        />
        <KpiCard
          icon="fa-star"
          label="Top touchpoint"
          value={top ? top.touchpoint : '—'}
          hint={top ? `${formatNumber(top.deals, 2)} deal(s) credited` : 'No won deals yet'}
          tone="violet"
        />
        <KpiCard
          icon="fa-circle-question"
          label="Won with no tracked touchpoint"
          value={formatNumber(data.leadsWithoutTouches)}
          tone="slate"
        />
      </div>

      <div className="analytics-grid">
        <ChartCard
          wide
          title={`Deals credited · ${current.label}`}
          subtitle="Revenue from won values, totalled in USD"
          empty={!rows.length}
          emptyText="No deals were won in this period, so there is nothing to attribute yet."
          table={{
            columns: ['Touchpoint', 'Deals credited', 'Revenue credited'],
            rows: rows.map((r) => [
              r.touchpoint,
              formatNumber(r.deals, 2),
              formatCurrency(r.revenueUsd, 'USD'),
            ]),
          }}
        >
          <ResponsiveContainer width="100%" height={Math.max(220, chart.length * 34 + 40)}>
            <BarChart
              data={chart}
              layout="vertical"
              margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="touchpoint" tick={{ fontSize: 11 }} width={180} />
              <Tooltip
                {...tooltipProps}
                labelFormatter={(_, p) => p?.[0]?.payload?.full || ''}
                formatter={(v, name) => [formatNumber(v, 2), name]}
              />
              <Bar dataKey="deals" name="Deals credited" fill="#7c3aed" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          wide
          title="Attribution details"
          empty={!rows.length}
          emptyText="No won deals in this period."
        >
          <DataTable
            caption="Attribution details"
            rows={rows}
            columns={[
              { key: 'touchpoint', label: 'Touchpoint' },
              {
                key: 'deals',
                label: 'Deals credited',
                align: 'right',
                render: (r) => formatNumber(r.deals, 2),
              },
              {
                key: 'revenue',
                label: 'Revenue credited (USD)',
                align: 'right',
                render: (r) => formatCurrency(r.revenueUsd, 'USD'),
              },
            ]}
          />
        </ChartCard>
      </div>

      <div className="analytics-gaps" role="note">
        <h4>
          <i className="fas fa-triangle-exclamation" aria-hidden="true"></i> Data gaps to keep in
          mind
        </h4>
        <Notes items={data.dataGaps} />
      </div>
    </div>
  );
};

export default AttributionTab;
